import React, { useCallback, useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ChevronLeft,
  Plus,
  Trash2,
  Save,
  FileDown,
  Send,
  Eye,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import useSEO from "../hooks/useSEO";

const DEFAULT_MEALS = [
  { key: "early_morning", label: "EARLY MORNING", time_range: "", description: "" },
  { key: "breakfast", label: "BREAKFAST", time_range: "", description: "" },
  { key: "mid_morning", label: "MID MORNING", time_range: "", description: "" },
  { key: "lunch", label: "LUNCH", time_range: "", description: "" },
  { key: "evening", label: "EVENING", time_range: "", description: "" },
  { key: "dinner", label: "DINNER", time_range: "", description: "" },
  { key: "bedtime", label: "BEDTIME", time_range: "", description: "" },
];

const empty = () => ({
  client_name: "",
  client_email: "",
  client_phone: "",
  age: "",
  height_cm: "",
  current_weight_kg: "",
  profession: "",
  lifestyle: "",
  ibw_kg: "",
  program_name: "Therapeutic Diet Plan",
  tagline: "Iron Absorption & Fat Reduction Program",
  about: "",
  key_benefits: [
    "Supports Heart Health",
    "Fat Reduction",
    "Boosts Energy",
    "Strengthens Immunity",
  ],
  meals: DEFAULT_MEALS.map((m) => ({ ...m })),
  quote: "Small consistent choices create extraordinary transformations.",
});

const toNum = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

export default function DietPlanBuilder() {
  const { user, authHeader, API } = useAuth();
  const [form, setForm] = useState(empty());
  const [plans, setPlans] = useState([]);
  const [savedId, setSavedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  useSEO({
    title: "Diet Plan Builder · DietDotIn Admin",
    description: "Create bespoke diet plans for clients.",
    noindex: true,
  });

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/diet-plans`, {
        headers: authHeader(),
      });
      setPlans(data);
    } catch {
      // ignore
    }
  }, [API, authHeader]);

  useEffect(() => {
    if (user && user.role === "admin") refresh();
  }, [user, refresh]);

  if (user === null) {
    return <div className="min-h-screen flex items-center justify-center text-[#CFAEFF]">Loading…</div>;
  }
  if (!user || user.role !== "admin") {
    return <Navigate to="/admin/login" replace />;
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setMeal = (i, k, v) =>
    setForm((f) => {
      const meals = f.meals.slice();
      meals[i] = { ...meals[i], [k]: v };
      return { ...f, meals };
    });
  const addMeal = () =>
    setForm((f) => ({
      ...f,
      meals: [...f.meals, { key: `custom_${f.meals.length}`, label: "SNACK", time_range: "", description: "" }],
    }));
  const removeMeal = (i) =>
    setForm((f) => ({ ...f, meals: f.meals.filter((_, idx) => idx !== i) }));

  const setBenefit = (i, v) =>
    setForm((f) => {
      const b = f.key_benefits.slice();
      b[i] = v;
      return { ...f, key_benefits: b };
    });
  const addBenefit = () =>
    setForm((f) => ({ ...f, key_benefits: [...f.key_benefits, ""] }));
  const removeBenefit = (i) =>
    setForm((f) => ({
      ...f,
      key_benefits: f.key_benefits.filter((_, idx) => idx !== i),
    }));

  const save = async () => {
    if (!form.client_name.trim()) {
      toast.error("Client name is required.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        age: toNum(form.age),
        height_cm: toNum(form.height_cm),
        current_weight_kg: toNum(form.current_weight_kg),
        ibw_kg: toNum(form.ibw_kg),
        client_email: form.client_email.trim() || null,
        client_phone: form.client_phone.trim() || null,
        key_benefits: form.key_benefits.filter((b) => b.trim() !== ""),
        meals: form.meals.filter((m) => m.description.trim() !== ""),
      };
      const { data } = await axios.post(`${API}/admin/diet-plans`, payload, {
        headers: authHeader(),
      });
      setSavedId(data.id);
      toast.success("Diet plan saved.");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save.");
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    if (!savedId) return toast.error("Save the plan first.");
    if (!form.client_email) return toast.error("Client email is required to send.");
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/diet-plans/${savedId}/send`,
        { to_email: form.client_email, cc_dietician: true },
        { headers: authHeader() },
      );
      if (data.status === "sent") {
        toast.success("Emailed to client + copy to dietician.");
      } else if (data.status === "partial") {
        toast(
          `Partial send. Errors: ${(data.errors || []).join(", ")}`,
          { duration: 8000 },
        );
      } else {
        toast(
          "Email service not configured (EMERGENT_EMAIL_KEY empty). PDF link is ready to share manually.",
          { duration: 8000 },
        );
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send.");
    } finally {
      setSending(false);
    }
  };

  const previewUrl = savedId ? `${API}/diet-plans/${savedId}/pdf` : null;

  return (
    <div className="relative min-h-screen pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 backdrop-blur-xl bg-[#140326]/75 border-b border-[#B48DFF]/15">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <Link to="/admin/leads" className="flex items-center gap-2 text-[#CFAEFF] hover:text-white">
            <ChevronLeft size={16} /> Back to Leads
          </Link>
          <div className="flex items-center gap-3">
            <button
              onClick={save}
              disabled={busy}
              data-testid="plan-save"
              className="btn-primary px-5 py-2 rounded-full text-sm font-heading inline-flex items-center gap-2 disabled:opacity-60"
            >
              <Save size={14} /> {busy ? "Saving…" : "Save Plan"}
            </button>
            {previewUrl && (
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="plan-preview"
                className="btn-ghost-glass px-5 py-2 rounded-full text-sm font-heading inline-flex items-center gap-2"
              >
                <Eye size={14} /> Preview PDF
              </a>
            )}
            <button
              onClick={send}
              disabled={!savedId || sending}
              data-testid="plan-send"
              className="btn-primary px-5 py-2 rounded-full text-sm font-heading inline-flex items-center gap-2 disabled:opacity-60"
            >
              <Send size={14} /> {sending ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 pt-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl text-white">
            Diet Plan <span className="gradient-text">Builder</span>
          </h1>
          <p className="mt-2 text-[#D8CCFF] text-sm">
            Craft a bespoke plan post-consultation. Save it, preview the branded PDF, and email it to the client — with a copy to <strong>pooja@dietdotin.com</strong>.
          </p>
        </motion.div>

        {/* Client details */}
        <section className="mt-8 glass rounded-2xl p-6">
          <h2 className="font-heading font-semibold text-white mb-4">Client Details</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { k: "client_name", label: "Full Name *", type: "text" },
              { k: "client_email", label: "Email", type: "email" },
              { k: "client_phone", label: "Phone / WhatsApp", type: "tel" },
              { k: "age", label: "Age", type: "number" },
              { k: "height_cm", label: "Height (cm)", type: "number" },
              { k: "current_weight_kg", label: "Current Weight (kg)", type: "number" },
              { k: "ibw_kg", label: "IBW (kg)", type: "number" },
              { k: "profession", label: "Profession", type: "text" },
              { k: "lifestyle", label: "Lifestyle", type: "text" },
            ].map((fld) => (
              <div key={fld.k}>
                <label className="text-[#CFAEFF] text-xs font-heading uppercase tracking-wider">{fld.label}</label>
                <input
                  type={fld.type}
                  value={form[fld.k]}
                  onChange={(e) => set(fld.k, e.target.value)}
                  className="field mt-2 w-full px-3 py-2.5 rounded-lg text-sm"
                  data-testid={`plan-field-${fld.k}`}
                />
              </div>
            ))}
          </div>
        </section>

        {/* Plan meta */}
        <section className="mt-6 glass rounded-2xl p-6">
          <h2 className="font-heading font-semibold text-white mb-4">Plan Details</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-[#CFAEFF] text-xs font-heading uppercase tracking-wider">Program Name</label>
              <input
                type="text"
                value={form.program_name}
                onChange={(e) => set("program_name", e.target.value)}
                className="field mt-2 w-full px-3 py-2.5 rounded-lg text-sm"
                data-testid="plan-field-program_name"
              />
            </div>
            <div>
              <label className="text-[#CFAEFF] text-xs font-heading uppercase tracking-wider">Tagline (Program Focus)</label>
              <input
                type="text"
                value={form.tagline}
                onChange={(e) => set("tagline", e.target.value)}
                placeholder="e.g. Iron Absorption & Fat Reduction Program"
                className="field mt-2 w-full px-3 py-2.5 rounded-lg text-sm"
                data-testid="plan-field-tagline"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[#CFAEFF] text-xs font-heading uppercase tracking-wider">About This Plan</label>
              <textarea
                rows={3}
                value={form.about}
                onChange={(e) => set("about", e.target.value)}
                className="field mt-2 w-full px-3 py-2.5 rounded-lg text-sm resize-none"
                data-testid="plan-field-about"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[#CFAEFF] text-xs font-heading uppercase tracking-wider">Closing Quote</label>
              <input
                type="text"
                value={form.quote}
                onChange={(e) => set("quote", e.target.value)}
                className="field mt-2 w-full px-3 py-2.5 rounded-lg text-sm"
                data-testid="plan-field-quote"
              />
            </div>
          </div>
        </section>

        {/* Meals */}
        <section className="mt-6 glass rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-semibold text-white">Daily Nutrition Plan</h2>
            <button
              onClick={addMeal}
              data-testid="plan-add-meal"
              className="btn-ghost-glass px-3 py-1.5 rounded-full text-xs inline-flex items-center gap-1.5"
            >
              <Plus size={12} /> Add Meal
            </button>
          </div>
          <div className="space-y-3">
            {form.meals.map((m, i) => (
              <div key={i} className="grid md:grid-cols-12 gap-3 items-start" data-testid={`plan-meal-${i}`}>
                <input
                  type="text"
                  value={m.label}
                  onChange={(e) => setMeal(i, "label", e.target.value)}
                  className="field md:col-span-3 w-full px-3 py-2 rounded-lg text-sm font-heading font-semibold"
                  placeholder="e.g. LUNCH"
                />
                <input
                  type="text"
                  value={m.time_range}
                  onChange={(e) => setMeal(i, "time_range", e.target.value)}
                  className="field md:col-span-2 w-full px-3 py-2 rounded-lg text-xs text-[#CFAEFF]"
                  placeholder="e.g. 1:00 PM"
                />
                <textarea
                  rows={2}
                  value={m.description}
                  onChange={(e) => setMeal(i, "description", e.target.value)}
                  className="field md:col-span-6 w-full px-3 py-2 rounded-lg text-xs resize-none"
                  placeholder="What to eat, portions, notes…"
                />
                <button
                  onClick={() => removeMeal(i)}
                  className="md:col-span-1 text-[#CFAEFF] hover:text-red-400 self-center"
                  aria-label="Remove meal"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Benefits */}
        <section className="mt-6 glass rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-semibold text-white">Key Benefits</h2>
            <button
              onClick={addBenefit}
              data-testid="plan-add-benefit"
              className="btn-ghost-glass px-3 py-1.5 rounded-full text-xs inline-flex items-center gap-1.5"
            >
              <Plus size={12} /> Add
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {form.key_benefits.map((b, i) => (
              <div key={i} className="flex gap-2 items-center">
                <input
                  type="text"
                  value={b}
                  onChange={(e) => setBenefit(i, e.target.value)}
                  className="field w-full px-3 py-2 rounded-lg text-sm"
                  data-testid={`plan-benefit-${i}`}
                />
                <button onClick={() => removeBenefit(i)} className="text-[#CFAEFF] hover:text-red-400" aria-label="Remove benefit">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Recent plans */}
        {plans.length > 0 && (
          <section className="mt-8 glass rounded-2xl p-6" data-testid="plan-recent">
            <h2 className="font-heading font-semibold text-white mb-4">Recent Diet Plans</h2>
            <div className="divide-y divide-[#B48DFF]/10">
              {plans.map((p) => (
                <div key={p.id} className="py-3 flex items-center justify-between text-sm">
                  <div>
                    <div className="text-white font-heading font-semibold">{p.client_name}</div>
                    <div className="text-[#CFAEFF] text-xs">{p.program_name} · {new Date(p.created_at).toLocaleDateString()}</div>
                  </div>
                  <a
                    href={`${API}/diet-plans/${p.id}/pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full glass text-[#CFAEFF] hover:text-white text-xs"
                  >
                    <FileDown size={12} /> Open PDF
                  </a>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
